import React, {useContext} from 'react';
import fileLight from "../../../../assets/fileLight.svg";
import audioFile from "../../../../assets/audioFile.svg";
import { ImSpinner2 } from "react-icons/im";
import { useAppStore } from "../../../../GlobalStore/GlobalStore.ts";
import { FileObject } from "../../../../Utils/uploadUtils/UploadUtils.ts";
import { useFilePreview } from "../../../../Hooks/useFilePreview.ts";
import {GlobalContext} from "../../../../App.tsx";
import {ensureConnection} from "../../../../Utils/websocket/websocketUtils.ts";
import CircularProgress from "./CircularProgress.tsx";

const MEDIA_DIMENSIONS = {
    container: {
        width: 'w-14',
        height: 'h-14',
    },
    preview: {
        width: 'w-12',
        height: 'h-12',
    },
    icon: {
        width: 'w-12',
        height: 'h-12',
    }
} as const;

const MEDIA_CLASSES = {
    container: 'flex flex-col items-center mx-2.5',
    mediaWrapper: `relative mt-2 flex justify-center items-center ${MEDIA_DIMENSIONS.container.width} ${MEDIA_DIMENSIONS.container.height} rounded-xl text-xs`,
    previewContainer: `${MEDIA_DIMENSIONS.preview.width} ${MEDIA_DIMENSIONS.preview.height} border border-neutral-700 rounded overflow-hidden`,
    mediaPreview: 'w-full h-full object-cover',
    iconPreview: 'ml-1 mt-0.5 p-0.5 cursor-pointer invert dark:invert-0',
    button: 'w-full h-full flex items-center justify-center',
    cancelButton: 'absolute -top-1.5 -right-1.5 bg-red-800 text-white border-none rounded-full w-5 h-5 text-xs flex justify-center items-center cursor-pointer z-10',
    loadingOverlay: 'absolute inset-0 flex items-center justify-center',
    fileName: 'text-xs text-center max-w-[4rem] whitespace-nowrap overflow-hidden text-ellipsis text-black dark:text-white',
    loadingText: 'text-[10px] text-neutral-500 dark:text-neutral-400 mb-0.5 capitalize'
} as const;

interface MediaItemProps {
    file: FileObject;
}

const MediaItem: React.FC<MediaItemProps> = ({file}) => {
    const { websocketRef, setupWebsocketHelper } = useContext(GlobalContext);
    const updateSelectedMedia = useAppStore((state: any) => state.updateSelectedMedia);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const previewUrl = useFilePreview(file);

    const handleCancelClick = async () => {
        updateSelectedMedia(file.fileId, null, "delete");
        await ensureConnection(websocketRef, setupWebsocketHelper);
        websocketRef.current.send(JSON.stringify({
            functionName: "uploadEvent",
            args: {
                event: "cancel",
                fileId: file.fileId
            }
        }));
    };

    const handleClick = () => {
        if (file.loadingInfo.task === 'done' && file.type) {
            setDynamicStateObj({
                visiblePopup: "enlargedContent",
                enlargedContent: {
                    type: file.type,
                    content: file.fileContent,
                    previewUrl: file.previewUrl
                },
            });
        }
    };

    const renderMediaPreview = () => {
        if (!file.type) return null;

        const isLoading = file.loadingInfo.task !== 'done';
        const commonButtonProps = {
            className: MEDIA_CLASSES.button,
            onClick: handleClick
        };

        const getPreviewContent = () => {
            switch (file.type) {
                case 'video':
                    return (
                        <div className={MEDIA_CLASSES.previewContainer}>
                            {file.previewUrl ? (
                                <img
                                    src={file.previewUrl}
                                    alt="Video Thumbnail"
                                    className={MEDIA_CLASSES.mediaPreview}
                                />
                            ) : (
                                <video
                                    src={previewUrl || undefined}
                                    className={MEDIA_CLASSES.mediaPreview}
                                />
                            )}
                            {!isLoading && (
                                <div className={MEDIA_CLASSES.loadingOverlay}>
                                    <div className="w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                                        <div className="w-0 h-0 border-t-4 border-t-transparent border-l-[8px] border-l-white border-b-4 border-b-transparent ml-0.5"/>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                case 'audio':
                    return (
                        <img
                            src={audioFile}
                            alt="Audio File"
                            className={`${MEDIA_CLASSES.iconPreview} ${MEDIA_DIMENSIONS.icon.width} ${MEDIA_DIMENSIONS.icon.height}`}
                        />
                    );
                case 'image':
                case 'gif':
                case 'link':
                    return (
                        <div className={MEDIA_CLASSES.previewContainer}>
                            <img
                                src={previewUrl || undefined}
                                alt={file.type === 'link' ? 'Link Preview' : file.fileName}
                                className={MEDIA_CLASSES.mediaPreview}
                            />
                        </div>
                    );
                default:
                    return (
                        <img
                            src={fileLight}
                            alt="Message File"
                            className={`${MEDIA_CLASSES.iconPreview} ${MEDIA_DIMENSIONS.icon.width} ${MEDIA_DIMENSIONS.icon.height}`}
                        />
                    );
            }
        };

        return (
            <button {...commonButtonProps}>
                {getPreviewContent()}
            </button>
        );
    };

    return (
        <div className={MEDIA_CLASSES.container}>
            <div className={MEDIA_CLASSES.mediaWrapper}>
                <div className={file.loadingInfo.task !== 'done' ? 'opacity-50' : ''}>
                    {renderMediaPreview()}
                </div>
                {file.loadingInfo.task !== 'done' && (
                    <div className={MEDIA_CLASSES.loadingOverlay}>
                        {file.loadingInfo.task === 'starting' ? (
                            <ImSpinner2 className="text-gray-500 animate-spin" size={24} />
                        ) : (
                            <CircularProgress progress={file.loadingInfo.progress || 0} size={32} />
                        )}
                    </div>
                )}
                <button
                    className={MEDIA_CLASSES.cancelButton}
                    onClick={handleCancelClick}
                >
                    &times;
                </button>
            </div>
            <div className="flex flex-col items-center">
                {file.type !== "link" && file.loadingInfo.task !== 'done' && (
                    <div className={MEDIA_CLASSES.loadingText}>
                        {file.loadingInfo.task}...
                    </div>
                )}
                <div
                    className={MEDIA_CLASSES.fileName}
                    title={file.fileName}
                >
                    {file.fileName}
                </div>
            </div>
        </div>
    );
};

export default MediaItem;